/**
 * Automated dunning controls (tracker item 36): the invoice page's "Hold
 * reminders" toggle and list of reminders sent for the invoice, and the
 * "Reminded (step N)" pill on AR aging rows. ADMIN and OFFICE_MANAGER.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createAPIClient, IDunningApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { invoiceDate, invoiceMoney } from '../pages/invoiceUi';
import { NOTICE_STATUS_LABELS, dunningReasonLabel, remindedLabel } from '../pages/dunningUi';
import { Toast } from './Toast';

export const HOLD_REMINDERS_HINT =
  'A held invoice gets no overdue reminders and is left out of its customer\'s reminder emails (for example while a payment is promised or the invoice is disputed).';

/** Pill on an AR aging row whose invoice was sent a reminder. */
export function RemindedBadge({ step }: { step: number | null | undefined }) {
  const label = remindedLabel(step);
  if (!label) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-800 whitespace-nowrap">
      {label}
    </span>
  );
}

interface PanelProps {
  invoice: types.Invoice;
  canManage: boolean;
  onInvoiceChange: (invoice: types.Invoice) => void;
}

/** Invoice detail: hold toggle and the reminders recorded for the invoice. */
export function InvoiceDunningPanel({ invoice, canManage, onInvoiceChange }: PanelProps) {
  const api = useMemo<IDunningApi>(() => createAPIClient(), []);
  const [notices, setNotices] = useState<types.DunningNotice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setNotices(await api.listInvoiceDunningNotices(invoice.id));
    } catch (cause) {
      setNotices([]);
      setError(getErrorMessage(cause, 'Could not load the reminders'));
    }
  }, [api, invoice.id]);

  useEffect(() => { void load(); }, [load]);

  async function toggleHold(hold: boolean) {
    setBusy(true);
    setError(null);
    try {
      onInvoiceChange(await api.setInvoiceDunningHold(invoice.id, hold));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not change the reminder hold'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="Overdue reminders" className="bg-white rounded-lg shadow p-5 mb-5">
      <h2 className="font-semibold text-lg mb-3">Overdue reminders</h2>
      <label className="flex items-start gap-2 text-sm min-w-0">
        <input
          type="checkbox"
          className="mt-1 h-5 w-5 shrink-0 sm:h-4 sm:w-4 rounded border-gray-300"
          checked={!!invoice.dunningHold}
          disabled={!canManage || busy}
          onChange={(event) => void toggleHold(event.target.checked)}
          aria-describedby="dunning-hold-hint"
        />
        <span>
          <span className="font-semibold">Hold reminders</span>
          <span id="dunning-hold-hint" className="block text-xs text-gray-500">{HOLD_REMINDERS_HINT}</span>
        </span>
      </label>
      {error && <Toast kind="error">{error}</Toast>}
      <h3 className="mt-4 text-sm font-semibold text-gray-900">Reminders for this invoice</h3>
      {notices === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : notices.length === 0 ? (
        <p className="text-sm text-gray-500">No reminders yet.</p>
      ) : (
        <ul className="mt-1 divide-y divide-gray-100 text-sm">
          {notices.map((n) => (
            <li key={n.id} className="py-2 flex flex-wrap justify-between gap-2">
              <span className="min-w-0 break-words">
                Step {n.stepPosition} ({n.stepDaysOverdue} days) · {invoiceDate(n.runDate)}
                <span className="text-gray-500"> · {n.daysOverdue} days overdue, {invoiceMoney(n.balance)}</span>
              </span>
              <span className={n.status === 'QUEUED' ? 'text-green-800' : 'text-gray-600'}>
                {NOTICE_STATUS_LABELS[n.status] ?? n.status}
                {n.reason && <span className="text-gray-500"> ({dunningReasonLabel(n.reason)})</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
